/**
 * EventTimeline - Visual timeline for cutscene events
 * Supports add, remove, reorder, and selection of events
 */

import React, { useCallback, useRef, useState } from "react";
import {
  CutsceneData,
  CutsceneEvent,
  createDefaultEvent,
} from "@/lib/cutscene/types";
import {
  getEventTypeName,
  getEventTypeStyle,
  formatDuration,
  calculateCutsceneDuration,
  reorderEvents,
  removeEvent,
  insertEvent,
} from "@/lib/cutscene/utils";

interface EventTimelineProps {
  cutscene: CutsceneData;
  onCutsceneChange: (cutscene: CutsceneData) => void;
  selectedEventId: string | null;
  onSelectEvent: (eventId: string | null) => void;
  currentEventIndex: number;
  isPlaying: boolean;
}

const EVENT_TYPES: CutsceneEvent["type"][] = [
  "spawn_pokemon",
  "move_pokemon",
  "animate_pokemon",
  "remove_pokemon",
  "play_vfx",
  "stop_vfx",
  "camera_pan",
  "camera_zoom",
  "camera_shake",
  "dialog",
  "hide_dialog",
  "wait",
  "fade",
];

export const EventTimeline: React.FC<EventTimelineProps> = ({
  cutscene,
  onCutsceneChange,
  selectedEventId,
  onSelectEvent,
  currentEventIndex,
  isPlaying,
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const handleAddEvent = useCallback(
    (type: CutsceneEvent["type"]) => {
      const newEvent = createDefaultEvent(type);
      const selectedIndex = cutscene.events.findIndex(
        (e) => e.id === selectedEventId
      );
      const insertIndex =
        selectedIndex >= 0 ? selectedIndex + 1 : cutscene.events.length;

      const newCutscene = insertEvent(cutscene, newEvent, insertIndex);
      onCutsceneChange(newCutscene);
      onSelectEvent(newEvent.id);
      setShowAddMenu(false);
    },
    [cutscene, selectedEventId, onCutsceneChange, onSelectEvent]
  );

  const handleRemoveEvent = useCallback(
    (eventId: string) => {
      const index = cutscene.events.findIndex((e) => e.id === eventId);
      if (index < 0) return;

      const newCutscene = removeEvent(cutscene, index);
      onCutsceneChange(newCutscene);

      if (selectedEventId === eventId) {
        onSelectEvent(null);
      }
    },
    [cutscene, selectedEventId, onCutsceneChange, onSelectEvent]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (isPlaying) return;
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
    },
    [isPlaying]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;
      setDragOverIndex(index);
    },
    [draggedIndex]
  );

  const handleDragEnd = useCallback(() => {
    if (draggedIndex !== null && dragOverIndex !== null) {
      const newCutscene = reorderEvents(cutscene, draggedIndex, dragOverIndex);
      onCutsceneChange(newCutscene);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [cutscene, draggedIndex, dragOverIndex, onCutsceneChange]);

  const handleDuplicateEvent = useCallback(
    (eventId: string) => {
      const index = cutscene.events.findIndex((e) => e.id === eventId);
      if (index < 0) return;

      const originalEvent = cutscene.events[index];
      const duplicatedEvent: CutsceneEvent = {
        ...JSON.parse(JSON.stringify(originalEvent)),
        id: `${originalEvent.id}_copy_${Date.now()}`,
      };

      const newCutscene = insertEvent(cutscene, duplicatedEvent, index + 1);
      onCutsceneChange(newCutscene);
      onSelectEvent(duplicatedEvent.id);
    },
    [cutscene, onCutsceneChange, onSelectEvent]
  );

  const totalDuration = calculateCutsceneDuration(cutscene);

  return (
    <div className="flex flex-col h-full bg-[#1a1f2e] border-r border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-[#161b26]">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-200">Timeline</h3>
          <span className="text-xs text-gray-500">
            {cutscene.events.length} events
          </span>
        </div>
        <div className="text-xs text-gray-400">
          ~{formatDuration(totalDuration)}
        </div>
      </div>

      {/* Add Event Button */}
      <div className="relative px-3 py-2 border-b border-gray-700">
        <button
          ref={addButtonRef}
          onClick={() => setShowAddMenu(!showAddMenu)}
          disabled={isPlaying}
          className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-md transition-colors flex items-center justify-center gap-2"
        >
          <span className="text-lg">+</span>
          Add Event
        </button>

        {/* Add Event Menu */}
        {showAddMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowAddMenu(false)}
            />
            <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-[#252b3b] border border-gray-600 rounded-lg shadow-xl max-h-[400px] overflow-y-auto">
              <div className="p-2 border-b border-gray-600">
                <span className="text-xs text-gray-400 font-medium">
                  Select Event Type
                </span>
              </div>
              <div className="p-1">
                {EVENT_TYPES.map((type) => {
                  const style = getEventTypeStyle(type);
                  return (
                    <button
                      key={type}
                      onClick={() => handleAddEvent(type)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-700 rounded flex items-center gap-3 transition-colors"
                    >
                      <span
                        className="w-6 h-6 rounded flex items-center justify-center text-xs"
                        style={{ backgroundColor: style.color + "30" }}
                      >
                        {style.icon}
                      </span>
                      <span className="text-gray-200">
                        {getEventTypeName(type)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Event List */}
      <div className="flex-1 overflow-y-auto">
        {cutscene.events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm">
            <span>No events yet</span>
            <span className="text-xs mt-1">Click "Add Event" to start</span>
          </div>
        ) : (
          <div className="py-1">
            {cutscene.events.map((event, index) => {
              const style = getEventTypeStyle(event.type);
              const isSelected = event.id === selectedEventId;
              const isCurrent = index === currentEventIndex && isPlaying;
              const isDragging = index === draggedIndex;
              const isDragOver = index === dragOverIndex;

              return (
                <div
                  key={event.id}
                  draggable={!isPlaying}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSelectEvent(event.id)}
                  className={`
                    group relative mx-2 mb-1 rounded-md cursor-pointer transition-all
                    ${isSelected ? "bg-blue-600/30 ring-1 ring-blue-500" : "hover:bg-gray-700/50"}
                    ${isCurrent ? "ring-2 ring-yellow-400" : ""}
                    ${isDragging ? "opacity-50" : ""}
                    ${isDragOver ? "border-t-2 border-blue-400" : ""}
                  `}
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    {/* Drag Handle */}
                    <div
                      className={`text-gray-500 cursor-grab active:cursor-grabbing ${isPlaying ? "opacity-30" : ""}`}
                    >
                      ⋮⋮
                    </div>

                    {/* Event Icon */}
                    <div
                      className="w-7 h-7 rounded flex items-center justify-center text-sm flex-shrink-0"
                      style={{
                        backgroundColor: style.color + "30",
                        color: style.color,
                      }}
                    >
                      {style.icon}
                    </div>

                    {/* Event Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-200 truncate">
                        {getEventTypeName(event.type)}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {getEventSummary(event)}
                      </div>
                    </div>

                    {/* Blocking indicator */}
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        event.blocking ? "bg-yellow-500" : "bg-gray-600"
                      }`}
                      title={event.blocking ? "Blocking" : "Non-blocking"}
                    />

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateEvent(event.id);
                        }}
                        disabled={isPlaying}
                        className="p-1 text-gray-400 hover:text-white hover:bg-gray-600 rounded disabled:opacity-30"
                        title="Duplicate"
                      >
                        ⧉
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveEvent(event.id);
                        }}
                        disabled={isPlaying}
                        className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded disabled:opacity-30"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Get a short summary of an event for display in timeline
 */
function getEventSummary(event: CutsceneEvent): string {
  switch (event.type) {
    case "spawn_pokemon":
      return `${event.pokemonName} at (${event.position.x}, ${event.position.y})`;
    case "move_pokemon":
      return `${event.actorId} → (${event.position.x}, ${event.position.y})`;
    case "animate_pokemon":
      return `${event.actorId}: ${event.animation}`;
    case "remove_pokemon":
      return event.actorId;
    case "play_vfx":
      return event.spritePath.split("/").pop() || "VFX";
    case "stop_vfx":
      return event.vfxId;
    case "camera_pan":
      return `→ (${event.target.x}, ${event.target.y})`;
    case "camera_zoom":
      return `${(event.zoom * 100).toFixed(0)}%`;
    case "camera_shake":
      return `${event.intensity}x for ${event.duration}ms`;
    case "dialog":
      return `${event.speaker}: "${event.text.substring(0, 20)}${event.text.length > 20 ? "..." : ""}"`;
    case "hide_dialog":
      return `${event.duration || 0}ms`;
    case "wait":
      return `${event.duration}ms`;
    case "fade":
      return `${event.direction} ${event.duration}ms`;
    default:
      return "";
  }
}





























